import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from '@mulesoft/anypoint-components/lib/Select';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Modal from '@mulesoft/anypoint-components/lib/Modal';
import ModalHeader from '@mulesoft/anypoint-components/lib/ModalHeader';
import ModalBody from '@mulesoft/anypoint-components/lib/ModalBody';
import ModalFooter from '@mulesoft/anypoint-components/lib/ModalFooter';
import { INTERVAL_UNIT_OPTIONS, WEEK_DAYS } from '../constants';
import {
  generateIntervalNumberOptions,
  generateMonthDayOptions
} from '../utils';
import styles from '../ScannerConfigurationForm.css';

/**
 * CustomScheduleModal Component
 *
 * A modal for configuring custom run schedules with options for:
 * - Interval number and unit (days, weeks, months)
 * - Day selection for weekly schedules
 * - Day of month selection for monthly schedules
 */
const CustomScheduleModal = ({
  customSchedule,
  onScheduleChange,
  onSave,
  onCancel
}) => {
  const intervalNumberOptions = useMemo(
    () => generateIntervalNumberOptions(customSchedule.intervalUnit),
    [customSchedule.intervalUnit]
  );

  const monthDayOptions = useMemo(() => generateMonthDayOptions(), []);

  const handleIntervalNumberChange = (event) => {
    onScheduleChange(
      'intervalNumber',
      parseInt(event.itemsSelected?.value || '1', 10)
    );
  };

  const handleIntervalUnitChange = (event) => {
    onScheduleChange('intervalUnit', event.itemsSelected?.value || 'days');
  };

  const handleMonthDayChange = (event) => {
    onScheduleChange(
      'monthDay',
      parseInt(event.itemsSelected?.value || '1', 10)
    );
  };

  const toggleRepeatDay = (day) => {
    const repeatDays =
      customSchedule.repeatDays.includes(day) &&
      customSchedule.repeatDays.length === 1
        ? []
        : [day];

    onScheduleChange('repeatDays', repeatDays);
  };

  return (
    <Modal
      renderInBody={false}
      onEsc={onCancel}
      onClickOverlay={onCancel}
      className={styles.modal}
      ariaLabelledBy="custom-range-modal-title"
      role="dialog"
    >
      <ModalHeader
        onClose={onCancel}
        testId="custom-range-modal-header"
        className={styles.header}
      >
        <h2 id="custom-range-modal-title" className={styles.title}>
          Custom Range
        </h2>
      </ModalHeader>
      <ModalBody className={styles.body} testId="custom-range-modal-body">
        {/* Every row */}
        <div className={styles['modal-row']}>
          <label
            htmlFor="custom-interval-number"
            className={styles['modal-row-label']}
          >
            Every
          </label>
          <div className={styles['modal-row-content']}>
            <Select
              testId="custom-interval-number"
              className={styles['modal-interval-number']}
              value={`${customSchedule.intervalNumber}`}
              options={intervalNumberOptions}
              onChange={handleIntervalNumberChange}
              disabled={customSchedule.intervalUnit === 'months'}
            />
            <Select
              testId="custom-interval-unit"
              className={styles['modal-interval-unit']}
              value={customSchedule.intervalUnit}
              options={INTERVAL_UNIT_OPTIONS}
              onChange={handleIntervalUnitChange}
            />
          </div>
        </div>

        {/* Repeat on - Only show for weeks */}
        {customSchedule.intervalUnit === 'weeks' && (
          <div className={styles['modal-row']}>
            <span id="repeat-on-label" className={styles['modal-row-label']}>
              Repeat on
            </span>
            <div className={styles['modal-row-content']}>
              <div
                className={styles['week-days-container']}
                role="group"
                aria-labelledby="repeat-on-label"
              >
                {WEEK_DAYS.map((day) => (
                  <Button
                    key={day.value}
                    type="button"
                    kind={
                      customSchedule.repeatDays.includes(day.value)
                        ? 'primary'
                        : 'secondary'
                    }
                    className={styles['week-day-button']}
                    onClick={() => toggleRepeatDay(day.value)}
                    testId={`repeat-day-${day.value}`}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* On the - Only show for months */}
        {customSchedule.intervalUnit === 'months' && (
          <div className={styles['modal-row']}>
            <label htmlFor="month-day" className={styles['modal-row-label']}>
              Day of Month
            </label>
            <div className={styles['modal-row-content']}>
              <Select
                testId="month-day"
                className={styles['modal-month-day']}
                value={`${customSchedule.monthDay}`}
                options={monthDayOptions}
                onChange={handleMonthDayChange}
              />
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter testId="custom-range-modal-footer" className={styles.footer}>
        <Button
          testId="cancel-custom-schedule"
          kind="tertiary"
          onClick={onCancel}
          noFill
        >
          Cancel
        </Button>
        <Button testId="save-custom-schedule" kind="primary" onClick={onSave}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CustomScheduleModal.propTypes = {
  /** Custom schedule configuration object */
  customSchedule: PropTypes.shape({
    intervalNumber: PropTypes.number.isRequired,
    intervalUnit: PropTypes.oneOf(['days', 'weeks', 'months']).isRequired,
    repeatDays: PropTypes.arrayOf(PropTypes.string).isRequired,
    monthDay: PropTypes.number.isRequired
  }).isRequired,
  /** Handler for schedule field changes */
  onScheduleChange: PropTypes.func.isRequired,
  /** Handler for save button */
  onSave: PropTypes.func.isRequired,
  /** Handler for cancel button */
  onCancel: PropTypes.func.isRequired
};

export default CustomScheduleModal;
